import Navbar from "./Navbar"
import Footer from "./Footer"
import { motion, useScroll, useTransform } from "framer-motion";
import featureHero from "../../static/images/features/feature.jpg";
import intuitive from "../../static/images/features/Intuitive.jpg";
import advanced from "../../static/images/features/Advanced.jpg";
import email from "../../static/images/features/Email.jpg";
import enterprise from "../../static/images/features/Enterprise.jpg";
import comprehensiveService from "../../static/images/features/Comprehensive_Service_Support.jpg";
import realTime from "../../static/images/features/Real-Time.jpg";
import multiUser from "../../static/images/features/Multi-User.jpg";
import certificate from "../../static/images/features/Certificate.jpg";
import mobile from "../../static/images/features/Mobile.jpg";
import analytics from "../../static/images/features/Comprehensive_Analytics.jpg";

const Features = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const features = [
    {
      title: "Intuitive Dashboard & UI",
      description:
        "User-friendly interface designed for marine professionals. Access all your survey information, reports, and certificates from a clean, organized dashboard that requires minimal training.",
      image: intuitive,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2m14 0V9m0 10a2 2 0 001 1.73m-14.73 0a2 2 0 001.73 1.73m0-10a2 2 0 01-1.73-1.73M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0V5m0 2a2 2 0 01-2-2h2a2 2 0 012 2v2m0 0h4a2 2 0 012 2v10a2 2 0 01-2 2h-2.5"
          />
        </svg>
      ),
      benefits: [
        "Clean, modern interface",
        "Easy-to-navigate dashboard",
        "Quick access to all services",
        "Real-time data visualization",
        "Customizable view options",
      ],
    },
    {
      title: "Advanced Report Management",
      description:
        "Handle all your survey reports with our optimized reporting system. Generate, store, and share detailed reports with professional formatting that meets international maritime standards.",
      image: advanced,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      benefits: [
        "Optimized report generation",
        "Perfect PDF formatting",
        "Multiple export formats",
        "Custom report templates",
        "Version control & history",
      ],
    },
    {
      title: "Email Notifications & Alerts",
      description:
        "Stay informed with automated email notifications for survey schedules, report completions, certificate renewals, and important maritime updates. Never miss critical deadlines.",
      image: email,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      benefits: [
        "Automated email alerts",
        "SMS notifications available",
        "Customizable alert preferences",
        "Deadline reminders",
        "Real-time status updates",
      ],
    },
    {
      title: "Enterprise Data Encryption",
      description:
        "Your sensitive marine data is protected with enterprise-grade end-to-end encryption. All information is securely stored and transmitted with advanced security protocols.",
      image: enterprise,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
      benefits: [
        "End-to-end encryption",
        "AES-256 data security",
        "Regular security audits",
        "Compliance with maritime standards",
        "Disaster recovery systems",
      ],
    },
    {
      title: "Comprehensive Service Support",
      description:
        "Get dedicated support for all your marine services including container surveys, tank inspections, cargo handling, and more. Our team is ready to assist with any service-related queries.",
      image: comprehensiveService,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      benefits: [
        "24/7 customer support",
        "Expert guidance on services",
        "Live chat assistance",
        "Email & phone support",
        "Video consultation options",
      ],
    },
    {
      title: "Real-Time Survey Tracking",
      description:
        "Track the progress of your surveys and inspections in real-time. Get instant updates on service status, completion timelines, and when your reports are ready for download.",
      image: realTime,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
      benefits: [
        "Live progress tracking",
        "Service milestone notifications",
        "Estimated completion times",
        "Historical tracking data",
        "Performance analytics",
      ],
    },
    {
      title: "Multi-User Access & Roles",
      description:
        "Manage team access with role-based permissions. Different team members can access relevant information based on their responsibilities—surveyors, clients, and administrators.",
      image: multiUser,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      benefits: [
        "Role-based access control",
        "User activity logging",
        "Secure team collaboration",
        "Permission-based visibility",
        "Audit trail maintenance",
      ],
    },
    {
      title: "Certificate & Compliance Management",
      description:
        "Centralized management of all marine certificates and compliance documents. Track expiry dates, receive automatic renewal reminders, and maintain regulatory compliance effortlessly.",
      image: certificate,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      ),
      benefits: [
        "Centralized certificate storage",
        "Expiry date tracking",
        "Automated renewal alerts",
        "IMO compliance monitoring",
        "Digital certificate issuance",
      ],
    },
    {
      title: "Mobile Accessibility",
      description:
        "Access your reports and survey information from anywhere on any device. Our responsive design ensures a seamless experience on smartphones, tablets, and desktops.",
      image: mobile,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
      benefits: [
        "Fully responsive design",
        "iOS & Android support",
        "Offline data access",
        "Touch-optimized interface",
        "Push notifications",
      ],
    },
    {
      title: "Comprehensive Analytics & Insights",
      description:
        "Gain detailed insights into your operations with analytics dashboards. Track survey trends, identify patterns, and make data-driven decisions for your marine business.",
      image: analytics,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
          />
        </svg>
      ),
      benefits: [
        "Visual analytics dashboards",
        "Service performance metrics",
        "Trend analysis & forecasting",
        "Custom report generation",
        "Data export capabilities",
      ],
    },
  ]

  const technicalFeatures = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
          />
        </svg>
      ),
      title: "Cloud Infrastructure",
      description: "Reliable cloud-based infrastructure ensuring 99.9% uptime and high availability",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Fast Performance",
      description: "Optimized for speed with quick loading times and responsive interface",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
      title: "Regular Updates",
      description: "Continuous improvements and feature updates based on user feedback",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
      title: "Security First",
      description: "Advanced security measures protecting your sensitive maritime data",
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[70vh] mt-20 overflow-hidden">
        <img
          src={featureHero}
          alt="Platform Features"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">Platform Features for Marine Services</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8">
                Powerful tools designed to enhance marine operations, optimize report handling, and streamline service
                delivery
              </p>
              <a
                href="#features"
                className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-all shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                Explore Features
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }} 
        id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-orange mb-6">Powerful Platform Features for Marine Excellence</h2>
            <p className="text-lg text-gray-500 leading-relaxed">
              Everything you need to manage surveys, reports, and marine operations efficiently with intuitive tools and
              robust security
            </p>
          </div>

          <div className="space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-8 lg:gap-12 items-center`}
              >
                <div className="w-full lg:w-1/2">
                  <img
                    src={feature.image || "/placeholder.svg"}
                    alt={feature.title}
                    className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
                  />
                </div>

                <div className="w-full lg:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="text-orange mb-0 mr-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-600 mb-0">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <svg
                          className="w-6 h-6 text-orange flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Technical Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-orange mb-6">Technical Excellence</h2>
            <p className="text-lg text-gray-600">
              Built on modern technology for reliability, security, and high performance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technicalFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-sm shadow-sm border hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2 text-center"
              >
                <div className="text-orange mb-4 flex justify-center">{feature.icon}</div>
                <h4 className="text-2xl font-semibold text-gray-600 mb-3">{feature.title}</h4>
                <p className="text-gray-600 text-md leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Why Choose Our Marine Services Platform</h2>
            <p className="text-lg text-gray-600 mb-12">
              Experience the difference with a platform designed specifically for maritime excellence and operational
              efficiency
            </p>

            <div className="grid md:grid-cols-2 gap-8 text-left">
              {[
                {
                  title: "Save Time",
                  description: "Automate report generation and survey tracking, reducing manual work by up to 70%",
                  icon: "⏱️",
                },
                {
                  title: "Reduce Costs",
                  description: "Streamline operations and eliminate inefficiencies in marine service delivery",
                  icon: "💰",
                },
                {
                  title: "Improve Accuracy",
                  description: "Ensure perfect reports and error-free documentation with automated verification",
                  icon: "🎯",
                },
                {
                  title: "Enhance Compliance",
                  description: "Stay compliant with maritime regulations through automated tracking and alerts",
                  icon: "✅",
                },
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-100"
                >
                  <div className="text-4xl mb-3">{benefit.icon}</div>
                  <h4 className="text-2xl font-semibold text-gray-600 mb-2">{benefit.title}</h4>
                  <p className="text-gray-600 text-md">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  )
}

export default Features;